import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { Upload, TableRows, ViewModule, Download } from "@mui/icons-material";
import NewLeadEntryButton from "../features/leads/components/NewLeadEntryButton.jsx";
import {
  getLeads,
  getLeadsGrouped,
  acceptLead,
  rejectLead,
  convertLeadToQuote,
} from "../features/leads/services/createLeadService.jsx";
import {
  bulkUpdateLeadStatus,
  bulkUpdateLeadInfo,
  bulkDeleteLeads,
} from "../features/leads/services/leadService.js";
import {
  downloadCsvText,
  exportProviderPricingCsv,
} from "../features/leads/services/aiLeadIntakeService.js";
import LeadImportModal from "../features/leads/components/LeadImportModal.jsx";
import LeadsTableView from "../features/leads/components/LeadsTableView.jsx";
import LeadsCardView from "../features/leads/components/LeadsCardView.jsx";
import LeadCardList from "../features/leads/components/LeadCardList.jsx";
import LeadMobileHeader from "../features/leads/components/LeadMobileHeader.jsx";
import LeadOpsStatusRibbon from "../features/leads/components/LeadOpsStatusRibbon.jsx";
import LeadOpsFacetFilters from "../features/leads/components/LeadOpsFacetFilters.jsx";
import LeadIntelligenceOverview from "../features/leads/components/intelligence/LeadIntelligenceOverview.jsx";
import ShareLeadWhatsAppModal from "../features/leads/components/ShareLeadWhatsAppModal.jsx";
import { useDeviceType } from "../features/leads/hooks/useDeviceType.js";
import { getLeadOperationalBucket } from "../features/leads/services/leadOperationalStatus.js";
import {
  LEAD_FILTER_ALL,
  leadMatchesCorridorFilter,
  leadMatchesTruckFilter,
} from "../features/leads/services/leadListFilters.js";
import { logError } from "@/shared/utils/logger.js";
import api from "@/shared/api.jsx";
import {
  groupLeadsByPhone,
  flattenGroupedLeads,
} from "../features/leads/utils/leadDeduplication.js";
import { Drawer, Button as AntButton, message } from "antd";
import {
  CloseOutlined,
  ExpandOutlined,
  CompressOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { RouteMapWrapper } from "../components/RouteMap";
import LeadsDateFilter from "../features/leads/components/LeadsDateFilter.jsx";
import {
  loadLeadsCrmStatus,
  saveLeadsCrmStatus,
  loadLeadsDateFilter,
  saveLeadsDateFilter,
  toLeadsDateQueryParams,
  leadMatchesDateFilter,
} from "../features/leads/utils/leadsPageFilters.js";

const Leads = () => {
  const navigate = useNavigate();
  const { isMobile, isTablet } = useDeviceType();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [actionNotes, setActionNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [filter, setFilter] = useState(() => loadLeadsCrmStatus());
  const [dateFilter, setDateFilter] = useState(() => loadLeadsDateFilter());
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [listView, setListView] = useState("table"); // "table" | "cards"
  const [corridorFilter, setCorridorFilter] = useState(LEAD_FILTER_ALL);
  const [truckFilter, setTruckFilter] = useState(LEAD_FILTER_ALL);
  const [mapRoute, setMapRoute] = useState(null);
  const [mapDrawerVisible, setMapDrawerVisible] = useState(false);
  const [mapDrawerExpanded, setMapDrawerExpanded] = useState(false);
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [bulkStatusAction, setBulkStatusAction] = useState("");
  const [bulkStatusNotes, setBulkStatusNotes] = useState("");
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({
    category: "",
    notes: "",
    moving_from: "",
    moving_to: "",
  });
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteReason, setBulkDeleteReason] = useState("");
  const [shareWhatsAppLead, setShareWhatsAppLead] = useState(null);
  const [opsBucketFilter, setOpsBucketFilter] = useState(null);
  const [pricingQueueFilter, setPricingQueueFilter] = useState("all"); // all | awaiting_provider | priced

  const searchTimeoutRef = useRef(null);

  const handleCrmStatusChange = useCallback((nextStatus) => {
    const saved = saveLeadsCrmStatus(nextStatus);
    setFilter(saved);
  }, []);

  const handleDateFilterChange = useCallback((nextDateFilter) => {
    const saved = saveLeadsDateFilter(nextDateFilter);
    setDateFilter(saved);
  }, []);

  // Try next available filter if current one has no items
  const tryNextAvailableFilter = useCallback(async () => {
    const filterOrder = ["new", "marketplace", "accepted", "rejected", "converted", "all"];
    const currentIndex = filterOrder.indexOf(filter);
    const dateParams = toLeadsDateQueryParams(dateFilter);

    for (let i = currentIndex + 1; i < filterOrder.length; i++) {
      const nextFilter = filterOrder[i];
      try {
        let leadsData = [];
        if (nextFilter === "new") {
          const { leads: newLeads } = await getLeads({
            status: "new",
            ...dateParams,
          });
          const { leads: pendingLeads } = await getLeads({
            status: "pending",
            ...dateParams,
          });
          leadsData = [...newLeads, ...pendingLeads];
        } else {
          const { leads } = await getLeads(
            nextFilter !== "all"
              ? { status: nextFilter, ...dateParams }
              : { ...dateParams }
          );
          leadsData = leads;
        }

        if (leadsData && leadsData.length > 0) {
          // In-session hop only — do not overwrite the user's persisted CRM preference.
          setFilter(nextFilter);
          setLeads(leadsData);
          break;
        }
      } catch (error) {
        console.error(`Error checking filter ${nextFilter}:`, error);
      }
    }
  }, [filter, dateFilter]);

  const fetchLeads = useCallback(
    async (searchQuery = null) => {
      try {
        setLoading(true);
        let leadsData = [];
        const dateParams = toLeadsDateQueryParams(dateFilter);

        // If search query provided, use backend search API
        if (searchQuery && searchQuery.trim().length >= 2) {
          try {
            const response = await api.get("/leads/search", {
              params: { q: searchQuery.trim(), includeQuotedStatus: true },
            });

            if (response.success) {
              let searchResults = response.leads || [];

              // Apply current status filter to search results
              if (filter !== "all") {
                searchResults = searchResults.filter((lead) => {
                  const leadStatus = lead.status || lead.primaryLead?.status;
                  return leadStatus === filter;
                });
              }

              searchResults = searchResults.filter((lead) =>
                leadMatchesDateFilter(lead, dateFilter)
              );

              // Use grouped leads for mobile/tablet
              if (isMobile || isTablet) {
                try {
                  let groupedResult;

                  if (filter === "new") {
                    // For "new" filter, group the already-filtered search results manually
                    // The search results are already filtered by status in the code above
                    const groupedData = groupLeadsByPhone(searchResults);
                    const displayLeads = flattenGroupedLeads(groupedData);
                    groupedResult = { leads: displayLeads, success: true };
                  } else {
                    // For other filters, use the existing grouped function
                    groupedResult = await getLeadsGrouped(
                      {
                        search: searchQuery.trim(),
                        ...dateParams,
                        ...(filter !== "all" ? { status: filter } : {}),
                      },
                      true
                    );
                  }

                  setLeads(groupedResult.leads);
                } catch (error) {
                  logError("Failed to group search results", {
                    error: error.message,
                  });
                  setLeads(searchResults);
                }
              } else {
                setLeads(searchResults);
              }
              return;
            }
          } catch (error) {
            logError("Search failed, falling back to regular fetch", {
              error: error.message,
            });
            // Fall through to regular fetch
          }
        }

        // Regular fetch without search
        if (filter === "new") {
          const { leads: newLeads } = await getLeads({
            status: "new",
            ...dateParams,
          });
          const { leads: pendingLeads } = await getLeads({
            status: "pending",
            ...dateParams,
          });
          leadsData = [...newLeads, ...pendingLeads];
        } else if (filter === "marketplace") {
          const { leads } = await getLeads({ marketplace: true });
          leadsData = leads;
        } else {
          const { leads } = await getLeads(
            filter !== "all"
              ? { status: filter, ...dateParams }
              : { ...dateParams }
          );
          leadsData = leads;
        }

        // Use grouped leads for mobile/tablet, regular for desktop
        if (isMobile || isTablet) {
          try {
            let groupedResult;

            if (filter === "new") {
              // For "new" filter, we already have leadsData with "new" and "pending" leads
              // Group them manually instead of fetching again with empty filter (which would get all leads)
              const groupedData = groupLeadsByPhone(leadsData);
              const displayLeads = flattenGroupedLeads(groupedData);
              groupedResult = { leads: displayLeads, success: true };
            } else if (filter === "marketplace") {
              const groupedData = groupLeadsByPhone(leadsData);
              const displayLeads = flattenGroupedLeads(groupedData);
              groupedResult = { leads: displayLeads, success: true };
            } else {
              // For other filters, use the existing grouped function
              groupedResult = await getLeadsGrouped(
                filter !== "all"
                  ? { status: filter, ...dateParams }
                  : { ...dateParams },
                true
              );
            }

            setLeads(groupedResult.leads);
          } catch (error) {
            logError("Failed to fetch grouped leads, falling back to regular", {
              error: error.message,
            });
            setLeads(leadsData);
          }
        } else {
          setLeads(leadsData);
        }

        if (leadsData.length === 0 && filter !== "all" && !searchQuery) {
          await tryNextAvailableFilter();
        }
      } catch (error) {
        logError("Error fetching leads", {
          error: error.message,
          stack: error.stack,
        });
        setLeads([]);
      } finally {
        setLoading(false);
      }
    },
    [filter, dateFilter, isMobile, isTablet, tryNextAvailableFilter]
  );

  // Refetch when CRM status or date filter changes (but not when search is active)
  useEffect(() => {
    // Clear any pending search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    // Only refetch if search is empty
    if (!searchValue || searchValue.trim().length < 2) {
      fetchLeads();
    }
  }, [filter, dateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search handler
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchValue && searchValue.trim().length >= 2) {
      // Debounce search by 500ms
      searchTimeoutRef.current = setTimeout(() => {
        fetchLeads(searchValue);
      }, 500);
    } else if (searchValue === "") {
      // If search is cleared, refetch with current filter
      fetchLeads();
    }

    // Cleanup timeout on unmount or when searchValue changes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchValue, fetchLeads]);

  // (unused) Normalization handled in table view

  // (unused) Helper retained for reference; handled inside table actions

  const handleViewLead = (lead) => {
    const leadId = lead._id || lead.id;
    navigate(`/leads/${leadId}`, {
      state: { lead }, // Pass lead data for optimistic update
    });
  };

  const handleFollowUpNavigate = (lead) => {
    const leadId = lead._id || lead.id;
    if (!leadId) return;
    const followUpId =
      lead?.followUpSummary?.nextFollowUpId ||
      lead?.followUpSummary?.latestFollowUpId;
    if (followUpId) {
      navigate(`/followups/${followUpId}`);
    } else {
      navigate(`/followups?entityType=lead&entityId=${leadId}`);
    }
  };

  // Handle route click - open map drawer
  const handleRouteClick = (lead, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const pickup =
      lead.moving_from || lead.from || lead?.pickupAddress?.address || null;
    const delivery =
      lead.moving_to || lead.to || lead?.deliveryAddress?.address || null;

    if (pickup && delivery) {
      setMapRoute({
        pickup,
        delivery,
        pickupAddress: lead?.pickupAddress || null,
        deliveryAddress: lead?.deliveryAddress || null,
      });
      setMapDrawerVisible(true);
    } else {
      message.warning("Pickup or delivery address is missing");
    }
  };

  // Generate Google Maps URL for route
  const getGoogleMapsUrl = (pickup, delivery) => {
    if (!pickup || !delivery) return null;
    const encodedPickup = encodeURIComponent(pickup);
    const encodedDelivery = encodeURIComponent(delivery);
    return `https://www.google.com/maps/dir/?api=1&origin=${encodedPickup}&destination=${encodedDelivery}`;
  };

  const handleAction = (lead, type) => {
    if (type === "convert") {
      // Navigate directly to quote form with leadId pre-populated
      const leadId = lead._id || lead.id;
      navigate(`/quotes/new?leadId=${leadId}`);
      return;
    }
    if (type === "whatsapp") {
      setShareWhatsAppLead(lead);
      return;
    }
    setSelectedLead(lead);
    setActionType(type);
    setActionNotes("");
    setActionDialogOpen(true);
  };

  const executeAction = async () => {
    try {
      setActionLoading(true);
      let response;

      switch (actionType) {
        case "accept":
          response = await acceptLead(selectedLead.id, "", actionNotes);
          break;
        case "reject":
          response = await rejectLead(selectedLead.id, "", actionNotes);
          break;
        default:
          throw new Error("Invalid action type");
      }

      if (response.success) {
        fetchLeads();
        // Show success message
      }
    } catch (error) {
      console.error("Error executing action:", error);
      // Show error message
    } finally {
      setActionLoading(false);
      setActionDialogOpen(false);
      setActionNotes("");
    }
  };

  const convertToQuote = async () => {
    try {
      setActionLoading(true);
      const response = await convertLeadToQuote(
        selectedLead.id,
        "",
        actionNotes
      );

      if (response.success) {
        // Show success message
        message.success(
          `Lead converted to quote successfully! The lead has been moved to the Accepted section.`
        );
        
        // Refresh leads list - the lead should now appear in the "accepted" filter
        await fetchLeads();
        
        // Optionally switch to "accepted" filter to show the converted lead immediately
        // Uncomment the line below if you want to auto-switch filters:
        // setFilter("accepted");
      } else {
        message.error(response.error || "Failed to convert lead to quote");
      }
    } catch (error) {
      console.error("Error converting to quote:", error);
      message.error(error.message || "Failed to convert lead to quote");
    } finally {
      setActionLoading(false);
      setActionDialogOpen(false);
      setActionNotes("");
    }
  };

  // Bulk action handlers
  const handleBulkAction = async (type) => {
    if (selectedRowKeys.length === 0) {
      return;
    }

    try {
      setActionLoading(true);
      const selectedLeads = leads.filter((lead) =>
        selectedRowKeys.includes(lead._id || lead.id)
      );

      // Execute action for each selected lead
      const promises = selectedLeads.map((lead) => {
        switch (type) {
          case "accept":
            return acceptLead(lead._id || lead.id, "", "");
          case "reject":
            return rejectLead(lead._id || lead.id, "", "");
          default:
            return Promise.resolve({ success: false });
        }
      });

      await Promise.all(promises);
      setSelectedRowKeys([]); // Clear selection
      fetchLeads();
    } catch (error) {
      console.error("Error executing bulk action:", error);
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk status update handler
  const handleBulkStatusUpdate = async () => {
    if (selectedRowKeys.length === 0 || !bulkStatusAction) {
      return;
    }

    try {
      setActionLoading(true);
      const result = await bulkUpdateLeadStatus(selectedRowKeys, {
        action: bulkStatusAction,
        notes: bulkStatusNotes,
      });

      if (result.success) {
        message.success(result.message || `Updated ${selectedRowKeys.length} lead(s)`);
        setSelectedRowKeys([]);
        setBulkStatusDialogOpen(false);
        setBulkStatusAction("");
        setBulkStatusNotes("");
        fetchLeads();
      } else {
        message.error(result.error || "Failed to update leads");
      }
    } catch (error) {
      console.error("Error updating bulk status:", error);
      message.error(error.message || "Failed to update leads");
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk edit handler
  const handleBulkEdit = async () => {
    if (selectedRowKeys.length === 0) {
      return;
    }

    try {
      setActionLoading(true);
      // Filter out empty fields
      const updateData = Object.fromEntries(
        Object.entries(bulkEditData).filter(([_, value]) => value !== "")
      );

      if (Object.keys(updateData).length === 0) {
        message.warning("Please provide at least one field to update");
        return;
      }

      const result = await bulkUpdateLeadInfo(selectedRowKeys, updateData);

      if (result.success) {
        message.success(result.message || `Updated ${selectedRowKeys.length} lead(s)`);
        setSelectedRowKeys([]);
        setBulkEditDialogOpen(false);
        setBulkEditData({ category: "", notes: "", moving_from: "", moving_to: "" });
        fetchLeads();
      } else {
        message.error(result.error || "Failed to update leads");
      }
    } catch (error) {
      console.error("Error updating bulk leads:", error);
      message.error(error.message || "Failed to update leads");
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) {
      return;
    }

    try {
      setActionLoading(true);
      const result = await bulkDeleteLeads(
        selectedRowKeys,
        bulkDeleteReason || "Bulk deletion"
      );

      if (result.success) {
        message.success(result.message || `Deleted ${selectedRowKeys.length} lead(s)`);
        setSelectedRowKeys([]);
        setBulkDeleteDialogOpen(false);
        setBulkDeleteReason("");
        fetchLeads();
      } else {
        message.error(result.error || "Failed to delete leads");
      }
    } catch (error) {
      console.error("Error deleting bulk leads:", error);
      message.error(error.message || "Failed to delete leads");
    } finally {
      setActionLoading(false);
    }
  };

  // Ops-bucket filter first (options for corridor/truck use this set)
  const opsFilteredLeads = useMemo(() => {
    if (!opsBucketFilter) return leads;
    return leads.filter((lead) => {
      const actual = lead.primaryLead || lead;
      return getLeadOperationalBucket(actual) === opsBucketFilter;
    });
  }, [leads, opsBucketFilter]);

  // Corridor + truck + provider-pricing queue facets on top of ops bucket
  const filteredLeads = useMemo(() => {
    return opsFilteredLeads.filter((lead) => {
      const actual = lead.primaryLead || lead;
      const matchesFacets =
        leadMatchesCorridorFilter(actual, corridorFilter) &&
        leadMatchesTruckFilter(actual, truckFilter);
      if (!matchesFacets) return false;
      if (pricingQueueFilter === "awaiting_provider") {
        return actual.providerPricing?.status === "awaiting_provider";
      }
      if (pricingQueueFilter === "priced") {
        return actual.providerPricing?.status === "priced";
      }
      return true;
    });
  }, [opsFilteredLeads, corridorFilter, truckFilter, pricingQueueFilter]);

  const handleExportProviderPricing = useCallback(
    async (includePii = false) => {
      if (selectedRowKeys.length === 0) {
        message.warning("Select one or more leads first");
        return;
      }
      setActionLoading(true);
      try {
        const result = await exportProviderPricingCsv({
          leadIds: selectedRowKeys,
          includePii,
        });
        if (!result?.csv) {
          throw new Error(result?.error || "Export returned empty CSV");
        }
        downloadCsvText(
          result.csv,
          result.filename ||
            (includePii
              ? "pricing-queue-internal.csv"
              : "pricing-queue-provider-no-ip.csv")
        );
        message.success(
          `Exported ${result.rowCount || selectedRowKeys.length} lead(s) to CSV`
        );
      } catch (error) {
        console.error("Provider pricing export failed:", error);
        message.error(error.message || "Failed to export pricing CSV");
      } finally {
        setActionLoading(false);
      }
    },
    [selectedRowKeys]
  );

  const ribbonLeads = useMemo(
    () => leads.map((lead) => lead.primaryLead || lead),
    [leads]
  );

  const facetSourceLeads = useMemo(
    () => opsFilteredLeads.map((lead) => lead.primaryLead || lead),
    [opsFilteredLeads]
  );

  const opsListLeads = useMemo(
    () =>
      filteredLeads.map((lead) => {
        const actualLead = lead.primaryLead || lead;
        return {
          ...actualLead,
          from: actualLead.moving_from || actualLead.from,
          to: actualLead.moving_to || actualLead.to,
          date: actualLead.move_date || actualLead.date,
        };
      }),
    [filteredLeads]
  );

  // Mobile/Tablet: Show card-based layout
  if (isMobile || isTablet) {
    return (
      <Box>
        <LeadMobileHeader
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          filterStatus={filter}
          onFilterChange={handleCrmStatusChange}
          dateFilter={dateFilter}
          onDateFilterChange={handleDateFilterChange}
        />

        <Box p={2}>
          <LeadIntelligenceOverview />
          <LeadOpsFacetFilters
            leads={facetSourceLeads}
            corridor={corridorFilter}
            truck={truckFilter}
            onCorridorChange={setCorridorFilter}
            onTruckChange={setTruckFilter}
            onClear={() => {
              setCorridorFilter(LEAD_FILTER_ALL);
              setTruckFilter(LEAD_FILTER_ALL);
            }}
          />
          <LeadCardList
            leads={filteredLeads}
            loading={loading}
            onWhatsApp={(lead) => {
              const actualLead = lead.primaryLead || lead;
              handleAction(actualLead, "whatsapp");
            }}
            onQuote={(lead) => {
              const actualLead = lead.primaryLead || lead;
              handleAction(actualLead, "convert");
            }}
            onFollowUp={handleFollowUpNavigate}
            onView={handleViewLead}
            onRoute={(lead, e) => {
              const actualLead = lead.primaryLead || lead;
              handleRouteClick(actualLead, e);
            }}
            onAccept={(lead) => {
              const actualLead = lead.primaryLead || lead;
              handleAction(actualLead, "accept");
            }}
            onReject={(lead) => {
              const actualLead = lead.primaryLead || lead;
              handleAction(actualLead, "reject");
            }}
            isTablet={isTablet}
          />
        </Box>

        {/* Action Dialog - same as desktop */}
        <Dialog
          open={actionDialogOpen}
          onClose={() => !actionLoading && setActionDialogOpen(false)}
          disableEscapeKeyDown={actionLoading}
        >
          <DialogTitle>
            {actionType === "accept" && "Accept Lead"}
            {actionType === "reject" && "Reject Lead"}
            {actionType === "convert" && "Convert to Quote"}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Notes (optional)"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
              sx={{ mt: 2 }}
              disabled={actionLoading}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setActionDialogOpen(false)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={
                actionType === "convert" ? convertToQuote : executeAction
              }
              variant="contained"
              color={
                actionType === "accept"
                  ? "success"
                  : actionType === "reject"
                    ? "error"
                    : "info"
              }
              disabled={actionLoading}
              startIcon={
                actionLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {actionLoading
                ? "Processing..."
                : actionType === "accept"
                  ? "Accept"
                  : actionType === "reject"
                    ? "Reject"
                    : "Convert"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Import Modal */}
        <LeadImportModal
          open={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          onSuccess={() => {
            fetchLeads();
            setImportModalOpen(false);
          }}
        />

        {/* Map Drawer */}
        <Drawer
          title={
            <div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  marginBottom: "4px",
                }}
              >
                Route Map
              </div>
              {mapRoute && (
                <div style={{ fontSize: "12px", color: "#666" }}>
                  {mapRoute.pickup} → {mapRoute.delivery}
                </div>
              )}
            </div>
          }
          placement={isMobile ? "bottom" : "right"}
          width={isMobile ? "100%" : mapDrawerExpanded ? "75%" : "600px"}
          height={isMobile ? "80vh" : "100%"}
          onClose={() => {
            setMapDrawerVisible(false);
            setMapRoute(null);
            setMapDrawerExpanded(false);
          }}
          open={mapDrawerVisible}
          extra={
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {!isMobile && (
                <AntButton
                  type="text"
                  icon={
                    mapDrawerExpanded ? (
                      <CompressOutlined />
                    ) : (
                      <ExpandOutlined />
                    )
                  }
                  onClick={() => setMapDrawerExpanded(!mapDrawerExpanded)}
                  title={mapDrawerExpanded ? "Compress map" : "Expand map"}
                />
              )}
              <AntButton
                type="text"
                icon={<CloseOutlined />}
                onClick={() => {
                  setMapDrawerVisible(false);
                  setMapRoute(null);
                  setMapDrawerExpanded(false);
                }}
              />
            </div>
          }
        >
          {mapRoute && (
            <div
              style={{
                height: isMobile ? "calc(80vh - 80px)" : "calc(100vh - 120px)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "1px solid #e8e8e8",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <RouteMapWrapper
                  pickup={mapRoute.pickup}
                  delivery={mapRoute.delivery}
                  pickupAddress={mapRoute.pickupAddress}
                  deliveryAddress={mapRoute.deliveryAddress}
                  drawerExpanded={mapDrawerExpanded}
                />
              </div>
              <div style={{ marginTop: "16px", display: "flex", gap: "8px" }}>
                <AntButton
                  type="primary"
                  icon={<EnvironmentOutlined />}
                  href={getGoogleMapsUrl(mapRoute.pickup, mapRoute.delivery)}
                  target="_blank"
                  block={isMobile}
                >
                  Open Route in Google Maps
                </AntButton>
              </div>
            </div>
          )}
        </Drawer>
      </Box>
    );
  }

  // Desktop: Show table-based layout
  return (
    <Box p={3} sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h4" component="h1">
            Leads Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {leads.length} lead{leads.length === 1 ? "" : "s"}
            {opsBucketFilter ? " · filtered by ops status" : ""}
          </Typography>
        </Box>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <Box display="flex" gap={1}>
            <Button
              variant={listView === "table" ? "contained" : "outlined"}
              startIcon={<TableRows />}
              onClick={() => setListView("table")}
              size="small"
            >
              Table
            </Button>
            <Button
              variant={listView === "cards" ? "contained" : "outlined"}
              startIcon={<ViewModule />}
              onClick={() => setListView("cards")}
              size="small"
            >
              Cards
            </Button>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => setImportModalOpen(true)}
          >
            Import CSV
          </Button>
          <Button
            variant={bulkSelectMode ? "contained" : "outlined"}
            onClick={() => {
              setBulkSelectMode(!bulkSelectMode);
              if (bulkSelectMode) {
                setSelectedRowKeys([]);
              }
            }}
            {...(bulkSelectMode && { color: "primary" })}
          >
            {bulkSelectMode ? "Cancel Selection" : "Select"}
          </Button>
          <NewLeadEntryButton />
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>CRM Status</InputLabel>
            <Select
              value={filter}
              label="CRM Status"
              onChange={(e) => handleCrmStatusChange(e.target.value)}
            >
              <MenuItem value="new">New</MenuItem>
              <MenuItem value="marketplace">On Marketplace</MenuItem>
              <MenuItem value="accepted">Accepted</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="converted">Converted</MenuItem>
              <MenuItem value="all">All Leads</MenuItem>
            </Select>
          </FormControl>
          <LeadsDateFilter
            value={dateFilter}
            onChange={handleDateFilterChange}
            minWidth={200}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Provider pricing</InputLabel>
            <Select
              value={pricingQueueFilter}
              label="Provider pricing"
              onChange={(e) => setPricingQueueFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="awaiting_provider">Not priced</MenuItem>
              <MenuItem value="priced">Provider priced</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Bulk Actions Bar */}
      {selectedRowKeys.length > 0 && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          p={2}
          sx={(theme) => ({
            backgroundColor: theme.palette.action.selected,
            borderRadius: 1,
          })}
        >
          <Typography variant="body2">
            {selectedRowKeys.length} lead(s) selected
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              color="info"
              size="small"
              onClick={() => {
                setBulkStatusAction("");
                setBulkStatusNotes("");
                setBulkStatusDialogOpen(true);
              }}
              disabled={actionLoading}
            >
              Update Status
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setBulkEditDialogOpen(true)}
              disabled={actionLoading}
            >
              Edit Selected
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setBulkDeleteDialogOpen(true)}
              disabled={actionLoading}
            >
              Delete Selected
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<Download />}
              onClick={() => handleExportProviderPricing(false)}
              disabled={actionLoading}
            >
              Export No-IP CSV
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Download />}
              onClick={() => handleExportProviderPricing(true)}
              disabled={actionLoading}
            >
              Export Internal CSV
            </Button>
            <Button
              variant="outlined"
              color="success"
              size="small"
              onClick={() => handleBulkAction("accept")}
              disabled={actionLoading}
            >
              Accept Selected
            </Button>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              onClick={() => handleBulkAction("reject")}
              disabled={actionLoading}
            >
              Reject Selected
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => setSelectedRowKeys([])}
            >
              Clear Selection
            </Button>
          </Box>
        </Box>
      )}

      {/* Leads Table (searchable) */}
      <LeadIntelligenceOverview />
      <LeadOpsStatusRibbon
        leads={ribbonLeads}
        activeBucket={opsBucketFilter}
        onBucketClick={setOpsBucketFilter}
      />
      <LeadOpsFacetFilters
        leads={facetSourceLeads}
        corridor={corridorFilter}
        truck={truckFilter}
        onCorridorChange={setCorridorFilter}
        onTruckChange={setTruckFilter}
        onClear={() => {
          setCorridorFilter(LEAD_FILTER_ALL);
          setTruckFilter(LEAD_FILTER_ALL);
        }}
      />
      {loading ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={8}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading leads...
          </Typography>
        </Box>
      ) : listView === "cards" ? (
        <LeadsCardView
          leads={opsListLeads}
          loading={loading}
          onViewLead={handleViewLead}
          onRefresh={fetchLeads}
          onFollowUpNavigate={handleFollowUpNavigate}
          onRouteClick={handleRouteClick}
          groupByCreatedPeriod
        />
      ) : (
        <LeadsTableView
          leads={opsListLeads}
          loading={loading}
          onViewLead={handleViewLead}
          onRefresh={fetchLeads}
          onFollowUpNavigate={handleFollowUpNavigate}
          onRouteClick={handleRouteClick}
          enableRowSelection={bulkSelectMode}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={setSelectedRowKeys}
        />
      )}

      <ShareLeadWhatsAppModal
        open={Boolean(shareWhatsAppLead)}
        lead={shareWhatsAppLead}
        onClose={() => setShareWhatsAppLead(null)}
      />

      {/* Action Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => !actionLoading && setActionDialogOpen(false)}
        disableEscapeKeyDown={actionLoading}
      >
        <DialogTitle>
          {actionType === "accept" && "Accept Lead"}
          {actionType === "reject" && "Reject Lead"}
          {actionType === "convert" && "Convert to Quote"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Notes (optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            sx={{ mt: 2 }}
            disabled={actionLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setActionDialogOpen(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={actionType === "convert" ? convertToQuote : executeAction}
            variant="contained"
            color={
              actionType === "accept"
                ? "success"
                : actionType === "reject"
                  ? "error"
                  : "info"
            }
            disabled={actionLoading}
            startIcon={
              actionLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {actionLoading
              ? "Processing..."
              : actionType === "accept"
                ? "Accept"
                : actionType === "reject"
                  ? "Reject"
                  : "Convert"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Modal */}
      <LeadImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={() => {
          fetchLeads();
          setImportModalOpen(false);
        }}
      />

      {/* Bulk Status Update Dialog */}
      <Dialog
        open={bulkStatusDialogOpen}
        onClose={() => !actionLoading && setBulkStatusDialogOpen(false)}
        disableEscapeKeyDown={actionLoading}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Status for {selectedRowKeys.length} Lead(s)</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={bulkStatusAction}
              label="Status"
              onChange={(e) => setBulkStatusAction(e.target.value)}
              disabled={actionLoading}
            >
              <MenuItem value="new">New</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="accept">Accept</MenuItem>
              <MenuItem value="reject">Reject</MenuItem>
              <MenuItem value="convert">Convert</MenuItem>
              <MenuItem value="deleted">Deleted</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Notes (optional)"
            multiline
            rows={3}
            value={bulkStatusNotes}
            onChange={(e) => setBulkStatusNotes(e.target.value)}
            disabled={actionLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBulkStatusDialogOpen(false);
              setBulkStatusAction("");
              setBulkStatusNotes("");
            }}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkStatusUpdate}
            variant="contained"
            disabled={actionLoading || !bulkStatusAction}
            startIcon={
              actionLoading ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {actionLoading ? "Updating..." : "Update Status"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog
        open={bulkEditDialogOpen}
        onClose={() => !actionLoading && setBulkEditDialogOpen(false)}
        disableEscapeKeyDown={actionLoading}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit {selectedRowKeys.length} Lead(s)</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Category"
            value={bulkEditData.category}
            onChange={(e) =>
              setBulkEditData({ ...bulkEditData, category: e.target.value })
            }
            disabled={actionLoading}
            select
            SelectProps={{
              native: false,
            }}
          >
            <MenuItem value="">None</MenuItem>
            <MenuItem value="residential">Residential</MenuItem>
            <MenuItem value="commercial">Commercial</MenuItem>
            <MenuItem value="industrial">Industrial</MenuItem>
            <MenuItem value="moving">Moving</MenuItem>
            <MenuItem value="delivery">Delivery</MenuItem>
            <MenuItem value="storage">Storage</MenuItem>
          </TextField>
          <TextField
            fullWidth
            margin="normal"
            label="Moving From"
            value={bulkEditData.moving_from}
            onChange={(e) =>
              setBulkEditData({ ...bulkEditData, moving_from: e.target.value })
            }
            disabled={actionLoading}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Moving To"
            value={bulkEditData.moving_to}
            onChange={(e) =>
              setBulkEditData({ ...bulkEditData, moving_to: e.target.value })
            }
            disabled={actionLoading}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Notes (will be appended)"
            multiline
            rows={3}
            value={bulkEditData.notes}
            onChange={(e) =>
              setBulkEditData({ ...bulkEditData, notes: e.target.value })
            }
            disabled={actionLoading}
            helperText="Notes will be appended to existing notes for each lead"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBulkEditDialogOpen(false);
              setBulkEditData({ category: "", notes: "", moving_from: "", moving_to: "" });
            }}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkEdit}
            variant="contained"
            disabled={actionLoading}
            startIcon={
              actionLoading ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {actionLoading ? "Updating..." : "Update Leads"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={() => !actionLoading && setBulkDeleteDialogOpen(false)}
        disableEscapeKeyDown={actionLoading}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete {selectedRowKeys.length} Lead(s)?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            This will soft delete the selected leads. They will be marked as deleted but
            not permanently removed from the database.
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            label="Reason (optional)"
            multiline
            rows={3}
            value={bulkDeleteReason}
            onChange={(e) => setBulkDeleteReason(e.target.value)}
            disabled={actionLoading}
            placeholder="e.g., Duplicate entries, Invalid data, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBulkDeleteDialogOpen(false);
              setBulkDeleteReason("");
            }}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkDelete}
            variant="contained"
            color="error"
            disabled={actionLoading}
            startIcon={
              actionLoading ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {actionLoading ? "Deleting..." : "Delete Leads"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Drawer */}
      <Drawer
        title={
          <div>
            <div
              style={{ fontSize: "16px", fontWeight: 600, marginBottom: "4px" }}
            >
              Route Map
            </div>
            {mapRoute && (
              <div style={{ fontSize: "12px", color: "#666" }}>
                {mapRoute.pickup} → {mapRoute.delivery}
              </div>
            )}
          </div>
        }
        placement={isMobile ? "bottom" : "right"}
        width={isMobile ? "100%" : mapDrawerExpanded ? "75%" : "600px"}
        height={isMobile ? "80vh" : "100%"}
        onClose={() => {
          setMapDrawerVisible(false);
          setMapRoute(null);
          setMapDrawerExpanded(false);
        }}
        open={mapDrawerVisible}
        extra={
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {!isMobile && (
              <AntButton
                type="text"
                icon={
                  mapDrawerExpanded ? <CompressOutlined /> : <ExpandOutlined />
                }
                onClick={() => setMapDrawerExpanded(!mapDrawerExpanded)}
                title={mapDrawerExpanded ? "Compress map" : "Expand map"}
              />
            )}
            <AntButton
              type="text"
              icon={<CloseOutlined />}
              onClick={() => {
                setMapDrawerVisible(false);
                setMapRoute(null);
                setMapDrawerExpanded(false);
              }}
            />
          </div>
        }
      >
        {mapRoute && (
          <div
            style={{
              height: isMobile ? "calc(80vh - 80px)" : "calc(100vh - 120px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                flex: 1,
                minHeight: 0,
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #e8e8e8",
                backgroundColor: "#f5f5f5",
              }}
            >
              <RouteMapWrapper
                pickup={mapRoute.pickup}
                delivery={mapRoute.delivery}
                pickupAddress={mapRoute.pickupAddress}
                deliveryAddress={mapRoute.deliveryAddress}
                drawerExpanded={mapDrawerExpanded}
              />
            </div>
            <div style={{ marginTop: "16px", display: "flex", gap: "8px" }}>
              <AntButton
                type="primary"
                icon={<EnvironmentOutlined />}
                href={getGoogleMapsUrl(mapRoute.pickup, mapRoute.delivery)}
                target="_blank"
                block={isMobile}
              >
                Open Route in Google Maps
              </AntButton>
            </div>
          </div>
        )}
      </Drawer>
    </Box>
  );
};

export default Leads;
